import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
} from "react-native";
import { Ionicons, Feather } from "@expo/vector-icons";
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";

import Colors from "../../Resources/Color";
import { useDevice } from "../_layout";

const defaultGetItemKey = (item) => {
  if (typeof item === "string") {
    return item;
  }

  return String(
    item?.id ?? item?.key ?? item?.value ?? item?.label ?? item?.name ?? "",
  );
};

const defaultGetItemLabel = (item) => {
  if (typeof item === "string") {
    return item;
  }

  return String(item?.label ?? item?.name ?? item?.value ?? item?.id ?? "");
};

const defaultGetItemSearchText = (item) => {
  if (typeof item === "string") {
    return item;
  }

  const keywords = Array.isArray(item?.keywords) ? item.keywords.join(" ") : "";
  return [defaultGetItemLabel(item), item?.searchText, keywords]
    .filter(Boolean)
    .join(" ");
};

const normalizeSearchText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const isEditableEventTarget = (target) => {
  if (!target || typeof window === "undefined") {
    return false;
  }

  const tagName = (target.tagName || "").toLowerCase();
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return true;
  }

  return target.isContentEditable === true;
};

const getScrollableNodeFromRef = (scrollContainerRef) => {
  const refCurrent = scrollContainerRef?.current;
  if (!refCurrent) {
    return null;
  }

  const scrollableNode = refCurrent.getScrollableNode?.();
  if (scrollableNode) {
    return scrollableNode;
  }

  if (refCurrent.scrollTop !== undefined) {
    return refCurrent;
  }

  const firstChild = refCurrent.children?.[0];
  if (firstChild && firstChild.scrollTop !== undefined) {
    return firstChild;
  }

  return null;
};

const SearchableDropdownRow = ({
  label,
  isHighlighted,
  isCurrentValue,
  showEnterHint,
  onSelect,
  onHover,
  index,
  totalResults,
  scrollContainerRef,
  keyboardNavigationRef,
}) => {
  const rowRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (
      !isHighlighted ||
      !keyboardNavigationRef?.current ||
      !rowRef.current ||
      !scrollContainerRef?.current
    ) {
      return;
    }

    const isFirstItem = index === 0;
    const isLastItem = index === totalResults - 1;
    const rowElement = rowRef.current;
    const scrollContainer = getScrollableNodeFromRef(scrollContainerRef);

    if (isFirstItem) {
      scrollContainerRef.current?.scrollTo?.({ y: 0, animated: true });
      if (scrollContainer) {
        scrollContainer.scrollTop = 0;
      }
      return;
    }

    if (isLastItem) {
      scrollContainerRef.current?.scrollToEnd?.({ animated: true });
      if (scrollContainer) {
        const containerHeight = scrollContainer.clientHeight;
        const scrollHeight = scrollContainer.scrollHeight;
        scrollContainer.scrollTop = scrollHeight - containerHeight;
      }
      return;
    }

    rowElement.scrollIntoView?.({
      block: "nearest",
      behavior: "smooth",
    });
  }, [index, isHighlighted, keyboardNavigationRef, scrollContainerRef, totalResults]);

  return (
    <TouchableOpacity
      ref={rowRef}
      activeOpacity={0.9}
      onPress={onSelect}
      onMouseEnter={() => {
        setIsHovered(true);
        onHover?.();
      }}
      onMouseLeave={() => setIsHovered(false)}
      style={[
        styles.row,
        (isHighlighted || isHovered) && styles.rowHighlighted,
      ]}
    >
      <Text
        style={[
          styles.rowLabel,
          isCurrentValue ? styles.rowLabelCurrentValue : null,
        ]}
        numberOfLines={1}
      >
        {label}
      </Text>
      {isCurrentValue && (
        <View style={styles.rowAccessory}>
          <Ionicons name="checkmark" size={16} color={Colors.bubbleColor} />
        </View>
      )}
    </TouchableOpacity>
  );
};

const SearchableDropdown = ({
  items = [],
  triggerLabel,
  triggerValue = "",
  selectedKey = null,
  searchPlaceholder,
  emptyLabel,
  suggestionsLabel,
  onSelect,
  open,
  onOpenChange,
  onEscapeKeyDown,
  portalRoot = null,
  width = 240,
  maxHeight = 260,
  disabled = false,
  getItemKey = defaultGetItemKey,
  getItemLabel = defaultGetItemLabel,
  getItemSearchText = defaultGetItemSearchText,
  onCreateValue,
  getCreateLabel,
  shouldShowCreate,
  initialQuery = "",
  autoFocusSearch,
  searchInputMaxLength,
  triggerStyle = null,
  triggerTextStyle = null,
  renderTrigger = null,
  showTriggerChevron = true,
  triggerChevronSize = 18,
  triggerChevronColor = Colors.grayColor,
}) => {
  const { isMobile } = useDevice();
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isTriggerHovered, setIsTriggerHovered] = useState(false);
  const [isClearHovered, setIsClearHovered] = useState(false);
  const searchInputRef = useRef(null);
  const scrollViewRef = useRef(null);
  const keyboardNavigationRef = useRef(false);
  const isOpenControlled = typeof open === "boolean";
  const resolvedOpen = isOpenControlled ? open : uncontrolledIsOpen;
  const resolvedInitialQuery = String(initialQuery ?? "");
  const shouldAutoFocusSearch =
    typeof autoFocusSearch === "boolean" ? autoFocusSearch : !isMobile;

  const normalizedQuery = normalizeSearchText(searchQuery);
  const filteredItems = useMemo(() => {
    if (normalizedQuery.length === 0) {
      return items;
    }

    return items.filter((item) =>
      normalizeSearchText(getItemSearchText(item)).includes(normalizedQuery),
    );
  }, [getItemSearchText, items, normalizedQuery]);
  const showCreateOption =
    typeof onCreateValue === "function" &&
    typeof shouldShowCreate === "function" &&
    shouldShowCreate(searchQuery, filteredItems, items) === true;
  const menuEntries = useMemo(() => {
    const nextEntries = filteredItems.map((item) => ({
      type: "item",
      key: getItemKey(item),
      item,
    }));

    if (showCreateOption) {
      nextEntries.push({
        type: "create",
        key: `__create__${normalizeSearchText(searchQuery)}`,
      });
    }

    return nextEntries;
  }, [filteredItems, getItemKey, searchQuery, showCreateOption]);

  const applyOpenChange = useCallback(
    (nextOpen, event, reason) => {
      const eventType =
        event && typeof event.type !== "undefined" ? event.type : null;
      const changeReason = typeof reason !== "undefined" ? reason : null;
      const shouldIgnoreFocusOutClose =
        nextOpen === false &&
        (changeReason === "focus-out" || eventType === "focusout");

      if (shouldIgnoreFocusOutClose) {
        return;
      }

      if (!isOpenControlled) {
        setUncontrolledIsOpen(nextOpen);
      }

      onOpenChange?.(nextOpen, event, reason);
    },
    [isOpenControlled, onOpenChange],
  );

  const { refs, floatingStyles, context } = useFloating({
    open: resolvedOpen,
    onOpenChange: applyOpenChange,
    placement: "bottom-start",
    middleware: [
      offset(6),
      flip({ fallbackAxisSideDirection: "start" }),
      shift({ padding: 10 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "menu" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
    role,
  ]);

  const stopTriggerEventPropagation = useCallback((event) => {
    event?.stopPropagation?.();
  }, []);

  const modalScopedPortalRoot =
    refs.reference.current?.closest?.(
      '[data-efortcoach-floating-root="true"]',
    ) ?? null;
  const resolvedPortalRoot =
    (portalRoot && portalRoot.isConnected === false ? null : portalRoot) ??
    (modalScopedPortalRoot && modalScopedPortalRoot.isConnected === false
      ? null
      : modalScopedPortalRoot) ??
    refs.reference.current?.ownerDocument?.body ??
    null;

  const closeMenu = useCallback(() => {
    applyOpenChange(false);
    setSearchQuery("");
    setSelectedIndex(0);
    keyboardNavigationRef.current = false;
  }, [applyOpenChange]);

  const toggleMenu = useCallback(() => {
    if (disabled) {
      return;
    }

    if (resolvedOpen) {
      closeMenu();
      return;
    }

    setSearchQuery("");
    setSelectedIndex(0);
    keyboardNavigationRef.current = false;
    applyOpenChange(true);
  }, [applyOpenChange, closeMenu, disabled, resolvedOpen]);

  useEffect(() => {
    if (!resolvedOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
      return;
    }

    setSearchQuery(resolvedInitialQuery);
    setSelectedIndex(0);

    if (!shouldAutoFocusSearch) {
      return;
    }

    let rafId = null;
    rafId = requestAnimationFrame(() => {
      searchInputRef.current?.focus?.();
    });

    return () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [resolvedInitialQuery, resolvedOpen, shouldAutoFocusSearch]);

  useEffect(() => {
    if (selectedIndex < menuEntries.length) {
      return;
    }

    setSelectedIndex(0);
  }, [menuEntries.length, selectedIndex]);

  const selectItem = useCallback(
    (item) => {
      onSelect?.(item);
      closeMenu();
    },
    [closeMenu, onSelect],
  );
  const createValue = useCallback(() => {
    onCreateValue?.(searchQuery);
    closeMenu();
  }, [closeMenu, onCreateValue, searchQuery]);

  const handleKeyDown = useCallback(
    (event) => {
      const key = event?.key;
      if (!key) {
        return;
      }

      if (key === "ArrowDown") {
        event.preventDefault?.();
        keyboardNavigationRef.current = true;
        setSelectedIndex((prev) =>
          menuEntries.length === 0 ? 0 : (prev + 1) % menuEntries.length,
        );
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault?.();
        keyboardNavigationRef.current = true;
        setSelectedIndex((prev) =>
          menuEntries.length === 0
            ? 0
            : (prev - 1 + menuEntries.length) % menuEntries.length,
        );
        return;
      }

      if (key === "Enter") {
        const selectedEntry = menuEntries[selectedIndex];
        if (!selectedEntry) {
          return;
        }
        event.preventDefault?.();
        if (selectedEntry.type === "create") {
          createValue();
          return;
        }

        selectItem(selectedEntry.item);
        return;
      }

      if (key === "Escape") {
        event.preventDefault?.();
        onEscapeKeyDown?.(event);
        closeMenu();
      }
    },
    [closeMenu, createValue, menuEntries, onEscapeKeyDown, selectItem, selectedIndex],
  );

  useEffect(() => {
    if (!resolvedOpen || typeof window === "undefined") {
      return;
    }

    const onKeyDown = (event) => {
      const key = event?.key;
      if (
        key !== "ArrowDown" &&
        key !== "ArrowUp" &&
        key !== "Enter" &&
        key !== "Escape"
      ) {
        return;
      }

      const floatingElement = refs?.floating?.current;
      const isInsideFloating =
        !!floatingElement && typeof floatingElement.contains === "function"
          ? floatingElement.contains(event?.target)
          : false;

      if (!isInsideFloating && isEditableEventTarget(event?.target)) {
        return;
      }

      event?.preventDefault?.();
      event?.stopPropagation?.();
      handleKeyDown(event);
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [handleKeyDown, resolvedOpen, refs]);

  const handleMouseMove = useCallback(() => {
    if (!isMobile) {
      keyboardNavigationRef.current = false;
    }
  }, [isMobile]);

  const triggerText = triggerValue || triggerLabel;

  return (
    <>
      <TouchableOpacity
        ref={refs.setReference}
        collapsable={false}
        disabled={disabled}
        activeOpacity={0.8}
        onPress={toggleMenu}
        onPressIn={stopTriggerEventPropagation}
        onMouseDown={stopTriggerEventPropagation}
        onPointerDown={stopTriggerEventPropagation}
        onTouchStart={stopTriggerEventPropagation}
        onMouseEnter={() => !isMobile && setIsTriggerHovered(true)}
        onMouseLeave={() => setIsTriggerHovered(false)}
        style={[
          styles.trigger,
          !showTriggerChevron ? styles.triggerNoChevron : null,
          disabled && styles.triggerDisabled,
          {
            borderColor:
              !disabled && isTriggerHovered
                ? "rgba(0, 0, 0, 0.5)"
                : Colors.borderColor,
          },
          triggerStyle,
        ]}
        {...getReferenceProps()}
      >
        {renderTrigger ? (
          renderTrigger({
            isOpen: resolvedOpen,
            isDisabled: disabled,
            isHovered: isTriggerHovered,
            triggerText,
          })
        ) : (
          <>
            <Text
              style={[
                styles.triggerText,
                !showTriggerChevron ? styles.triggerTextNoChevron : null,
                triggerValue ? styles.triggerValueText : styles.triggerPlaceholderText,
                triggerTextStyle,
              ]}
              numberOfLines={1}
            >
              {triggerText}
            </Text>
            {showTriggerChevron ? (
              <Feather
                name={resolvedOpen ? "chevron-up" : "chevron-down"}
                size={triggerChevronSize}
                color={triggerChevronColor}
              />
            ) : null}
          </>
        )}
      </TouchableOpacity>

      {resolvedOpen && (
        <FloatingPortal root={resolvedPortalRoot ?? undefined}>
          <FloatingFocusManager context={context} modal={false}>
            <View
              ref={refs.setFloating}
              style={[styles.menuContainer, floatingStyles, { width }]}
              {...getFloatingProps({
                onMouseDown: stopTriggerEventPropagation,
                onClick: stopTriggerEventPropagation,
                onPointerDown: stopTriggerEventPropagation,
                onTouchStart: stopTriggerEventPropagation,
              })}
            >
              <View style={styles.searchRow}>
                <Ionicons
                  name="search-outline"
                  size={14}
                  color={Colors.grayColor}
                  style={styles.searchIcon}
                />
                <TextInput
                  ref={searchInputRef}
                  value={searchQuery}
                  onChangeText={(text) => {
                    keyboardNavigationRef.current = false;
                    setSearchQuery(text);
                    setSelectedIndex(0);
                  }}
                  placeholder={searchPlaceholder}
                  placeholderTextColor={Colors.grayColor}
                  autoCorrect={false}
                  autoCapitalize="none"
                  maxLength={searchInputMaxLength}
                  style={styles.searchInput}
                />
                {searchQuery.length > 0 ? (
                  <TouchableOpacity
                    onPress={() => {
                      setSearchQuery("");
                      setSelectedIndex(0);
                      keyboardNavigationRef.current = false;
                      searchInputRef.current?.focus?.();
                    }}
                    onMouseEnter={() => !isMobile && setIsClearHovered(true)}
                    onMouseLeave={() => setIsClearHovered(false)}
                    style={styles.clearButton}
                  >
                    <Ionicons
                      name="close-circle-outline"
                      size={14}
                      color={
                        isClearHovered ? Colors.darkgrayColor : Colors.grayColor
                      }
                    />
                  </TouchableOpacity>
                ) : null}
              </View>

              {menuEntries.length > 0 ? (
                <ScrollView
                  ref={scrollViewRef}
                  style={{ maxHeight }}
                  contentContainerStyle={styles.resultsContent}
                  keyboardShouldPersistTaps="handled"
                  onMouseMove={handleMouseMove}
                >
                  {normalizedQuery.length === 0 && suggestionsLabel ? (
                    <View style={styles.suggestionsHeader}>
                      <Text style={styles.suggestionsHeaderText}>
                        {suggestionsLabel}
                      </Text>
                    </View>
                  ) : null}
                  {menuEntries.map((entry, index) => {
                    const item = entry.type === "item" ? entry.item : null;
                    const itemKey = entry.key;
                    const itemLabel =
                      entry.type === "create"
                        ? getCreateLabel?.(searchQuery) ?? searchQuery
                        : getItemLabel(item);
                    const showEnterHint =
                      menuEntries.length > 0 && selectedIndex === index;

                    return (
                      <SearchableDropdownRow
                        key={itemKey}
                        label={itemLabel}
                        isHighlighted={selectedIndex === index}
                        isCurrentValue={
                          entry.type === "item" && selectedKey === getItemKey(item)
                        }
                        showEnterHint={showEnterHint}
                        onSelect={() => {
                          if (entry.type === "create") {
                            createValue();
                            return;
                          }

                          selectItem(item);
                        }}
                        onHover={() => {
                          if (!isMobile && !keyboardNavigationRef.current) {
                            setSelectedIndex(index);
                          }
                        }}
                        index={index}
                        totalResults={menuEntries.length}
                        scrollContainerRef={scrollViewRef}
                        keyboardNavigationRef={keyboardNavigationRef}
                      />
                    );
                  })}
                </ScrollView>
              ) : (
                <View style={styles.emptyState}>
                  <Text style={styles.emptyStateText}>{emptyLabel}</Text>
                </View>
              )}
            </View>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  trigger: {
    width: "100%",
    minHeight: 38,
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: Colors.whiteColor,
  },
  triggerDisabled: {
    opacity: 0.55,
  },
  triggerText: {
    fontSize: 13,
    flex: 1,
    marginRight: 8,
  },
  triggerNoChevron: {
    justifyContent: "flex-start",
  },
  triggerTextNoChevron: {
    flex: 0,
    marginRight: 0,
  },
  triggerValueText: {
    color: Colors.blackColor,
    fontWeight: "500",
  },
  triggerPlaceholderText: {
    color: Colors.grayColor,
    fontWeight: "400",
  },
  menuContainer: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
    overflow: "hidden",
    zIndex: 99999,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    minHeight: 42,
    borderBottomWidth: 0.5,
    borderBottomColor: Colors.borderColor,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 0,
  },
  clearButton: {
    padding: 2,
  },
  resultsContent: {
    paddingVertical: 4,
  },
  suggestionsHeader: {
    paddingHorizontal: 14,
    marginTop: 4,
    paddingBottom: 8,
  },
  suggestionsHeaderText: {
    fontSize: 11,
    fontWeight: "600",
    color: Colors.grayColor,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  row: {
    minHeight: 38,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  rowHighlighted: {
    backgroundColor: Colors.hoveredGray,
  },
  rowLabel: {
    fontSize: 13,
    color: Colors.blackColor,
    fontWeight: "400",
    flexShrink: 1,
  },
  rowLabelCurrentValue: {
    color: Colors.bubbleColor,
    fontWeight: "600",
  },
  rowAccessory: {
    marginLeft: 8,
    minWidth: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyState: {
    paddingVertical: 20,
    paddingHorizontal: 18,
  },
  emptyStateText: {
    fontSize: 12,
    color: Colors.grayColor,
    textAlign: "center",
  },
});

export default SearchableDropdown;
